import * as React from "react";

import { Button, Form, Label } from "semantic-ui-react";
import { Input } from "@neworbit/simpleui-input";
import { Moment } from "moment/moment";
import * as moment from "moment/moment";

import { createValidator } from "not-valid";

import {
    CreateUpdateOccupationalIllness,
    DiagnosedOrSuspected, DiagnosedOrSuspectedOptions,
    InjuryOrIllness, InjuryOrIllnessOptions,
    OccupationalIllnessCategory,
    OccupationalIllnessCategoryOptions
} from "../models";
import { OhCaseListModel } from "../../crud/ohCase";

import OhCaseDropdown from "./OhCaseDropdown";

interface IProps {
    onChange: React.Dispatch<React.SetStateAction<CreateUpdateOccupationalIllness>>;
    ohCases: OhCaseListModel[];
    occupationalIllness: CreateUpdateOccupationalIllness;
    showErrors: boolean;
}

const Divider: React.FunctionComponent = () => <div className={"fields-divider"} />;

export const OccupationalIllnessForm = ({ onChange, occupationalIllness, ...props }: IProps) => {

    const notInFutureValidator = createValidator((value: moment.Moment) =>
        !value || value.isBefore(moment()), "Must not be in the future");

    const onCategoryChange = React.useCallback((category: keyof typeof OccupationalIllnessCategory) => {
        onChange(prevModel => ({
            ...prevModel,
            category
        }));
    }, [onChange]);

    const onInjuryOrIllnessChange = React.useCallback((injuryOrIllness: InjuryOrIllness) => {
        onChange(prevModel => ({
            ...prevModel,
            injuryOrIllness
        }));
    }, [onChange]);

    const onDiagnosedOrSuspected = React.useCallback((diagnosedOrSuspected: DiagnosedOrSuspected) => {
        onChange(prevModel => ({
            ...prevModel,
            diagnosedOrSuspected
        }));
    }, [onChange]);

    const onLinkedOHCaseChange = React.useCallback((linkedOhCaseId: string) => {
        onChange(prevModel => ({
            ...prevModel,
            linkedOhCaseId
        }));
    }, [onChange]);

    const onDescriptionChange = React.useCallback((description: string) => {
        onChange(prevModel => ({
            ...prevModel,
            description
        }));
    }, [onChange]);

    const onOshaReportableChange = React.useCallback((oshaReportable: boolean) => {
        onChange(prevModel => ({
            ...prevModel,
            oshaReportable
        }));
    }, [onChange]);

    const onRiddorReportableChange = React.useCallback((riddorReportable: boolean) => {
        onChange(prevModel => ({
            ...prevModel,
            riddorReportable
        }));
    }, [onChange]);

    const onReportedChange = React.useCallback((reported: boolean) => {
        onChange(prevModel => ({
            ...prevModel,
            reported
        }));
    }, [onChange]);

    const onAstraZenecaLocalSheReportableChange = React.useCallback((astraZenecaLocalSheReportableChange: boolean) => {
        onChange(prevModel => ({
            ...prevModel,
            astraZenecaLocalSheReportableChange
        }));
    }, [onChange]);

    const onDateReportedToTheSheTeam = React.useCallback((illnessReportedToTheSheTeam: Moment) => {
        onChange(prevModel => ({
            ...prevModel,
            dateReportedToTheSheTeam: illnessReportedToTheSheTeam
        }));
    }, [onChange]);

    const onIllnessStartedChange = React.useCallback((illnessStarted: Moment) => {
        onChange(prevModel => ({
            ...prevModel,
            illnessStarted
        }));
    }, [onChange]);

    const onFirstDayOfAbsenceChange = React.useCallback((firstDayOfAbsence: Moment) => {
        onChange(prevModel => ({
            ...prevModel,
            firstDayOfAbsence
        }));
    }, [onChange]);

    const onLastDayOfAbsenceChange = React.useCallback((lastDayOfAbsence: Moment) => {
        onChange(prevModel => ({
            ...prevModel,
            lastDayOfAbsence
        }));
    }, [onChange]);

    const onAbsentFromWorkChange = React.useCallback((hasBeenAbsent: boolean) => {
        onChange(prevModel => ({
            ...prevModel,
            hasBeenAbsent
        }));
    }, [onChange]);

    const onDescriptionOfFurtherActionChange = React.useCallback((descriptionOfFurtherAction: string) => {
        onChange(prevModel => ({
            ...prevModel,
            descriptionOfFurtherAction
        }));
    }, [onChange]);

    return (
        <>
            <Form>
                <Form.Field>
                    <label>
                        <span>Category <Button
                            target="_blank"
                            rel="noopener noreferrer"
                            href="/assets/Occupational Illnesses in myOH Categories.pdf"
                            circular
                            icon="info"
                            size="mini"
                        />
                        </span>
                    </label>
                    <Input.Dropdown
                        required
                        showErrors={props.showErrors}
                        onChange={onCategoryChange}
                        options={OccupationalIllnessCategoryOptions}
                        value={occupationalIllness.category}
                    />
                </Form.Field>
                <Divider />
                <Input.Dropdown
                    required
                    label="Injury Or Illness"
                    placeholder="Please select"
                    showErrors={props.showErrors}
                    onChange={onInjuryOrIllnessChange}
                    options={InjuryOrIllnessOptions}
                    value={occupationalIllness.injuryOrIllness}
                />
                <Divider />
                <OhCaseDropdown ohCases={props.ohCases} value={occupationalIllness.linkedOhCaseId} onChange={onLinkedOHCaseChange} />
                <Divider />
                <Input.Dropdown
                    required
                    label="Diagnosed Or Suspected"
                    placeholder="Please select"
                    showErrors={props.showErrors}
                    onChange={onDiagnosedOrSuspected}
                    options={DiagnosedOrSuspectedOptions}
                    value={occupationalIllness.diagnosedOrSuspected}
                />
                <Divider />
                <Input.Textarea
                    label="Description"
                    onChange={onDescriptionChange}
                    value={occupationalIllness.description}
                />
                <Divider />
                <Input.Checkbox
                    value={occupationalIllness.oshaReportable}
                    label="OSHA - AstraZeneca Global SHE Reportable"
                    onChange={onOshaReportableChange}
                />
                <Divider />
                <Input.Checkbox
                    value={occupationalIllness.riddorReportable}
                    label="RIDDOR Reportable"
                    onChange={onRiddorReportableChange}
                />
                <Divider />
                <Input.Checkbox
                    value={occupationalIllness.reported}
                    label="Reported to the SHE Team"
                    onChange={onReportedChange}
                />
                <Divider />
                <Input.Checkbox
                    value={occupationalIllness.astraZenecaLocalSheReportableChange}
                    label="AstraZeneca Local SHE Reportable"
                    onChange={onAstraZenecaLocalSheReportableChange}
                />
                <Input.Date
                    value={occupationalIllness.dateReportedToTheSheTeam}
                    label="Date Reported to the SHE Team"
                    onChange={onDateReportedToTheSheTeam}
                    validation={[notInFutureValidator]}
                />
                <Input.Date
                    value={occupationalIllness.illnessStarted}
                    label="Date Illness/Injury Started"
                    onChange={onIllnessStartedChange}
                    validation={[notInFutureValidator]}
                />
                {occupationalIllness.firstDayOfAbsence && !occupationalIllness.illnessStarted &&
                    <Label basic color="red" pointing content="Illness started date required" />}
                <Divider />
                <Input.Checkbox
                    value={occupationalIllness.hasBeenAbsent}
                    label="Has employee been absent from work due to illness?"
                    onChange={onAbsentFromWorkChange}
                />
                <Input.Date
                    value={occupationalIllness.firstDayOfAbsence}
                    disabled={!occupationalIllness.hasBeenAbsent}
                    showErrors={occupationalIllness.hasBeenAbsent && props.showErrors}
                    label="First Day of Absence"
                    onChange={onFirstDayOfAbsenceChange}
                    validation={[notInFutureValidator]}
                />
                {occupationalIllness.firstDayOfAbsence
                && occupationalIllness.illnessStarted
                && occupationalIllness.firstDayOfAbsence.isBefore(occupationalIllness.illnessStarted)
                && <Label basic color="red" pointing content="Must not be before illness started" />}
                {!occupationalIllness.firstDayOfAbsence && occupationalIllness.lastDayOfAbsence &&
                    <Label basic color="red" pointing content="First day of absence required" />}
                <Divider />
                <Input.Date
                    value={occupationalIllness.lastDayOfAbsence}
                    disabled={!occupationalIllness.hasBeenAbsent}
                    label="Last Day of Absence"
                    onChange={onLastDayOfAbsenceChange}
                    validation={[notInFutureValidator]}
                />
                {occupationalIllness.lastDayOfAbsence
                && occupationalIllness.firstDayOfAbsence
                && occupationalIllness.lastDayOfAbsence.isBefore(occupationalIllness.firstDayOfAbsence)
                && <Label basic color="red" pointing content="Must not be before first day of absence" />}
                <Divider />
                <Input.Textarea
                    label="Description of Further Action"
                    onChange={onDescriptionOfFurtherActionChange}
                    value={occupationalIllness.descriptionOfFurtherAction}
                />
                <Divider />
            </Form>
        </>
    );
};
